import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import Layout from './components/Layout';

import LoginPage from './pages/LoginPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProductsPage from './pages/ProductsPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import PaymentResultPage from './pages/PaymentResultPage';
import CommerceDeliverySettingsPage from './pages/CommerceDeliverySettingsPage';
import TestApiPage from './pages/TestApiPage';
import ConnectionsPage from './pages/ConnectionsPage';
import BotsConfigPage from './pages/BotsConfigPage';
import BotDetailPage from './pages/BotDetailPage';
import BotStatsPage from './pages/stats/BotStatsPage';
import ClinicChatsStatsPage from './pages/stats/ClinicChatsStatsPage';
import TagsConfigPage from './pages/TagsConfigPage';
import OnboardingPage from './pages/OnboardingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import ClinicsDashboardPage from './pages/clinics/ClinicsDashboardPage';
import ClinicsPatientsPage from './pages/clinics/ClinicsPatientsPage';
import ClinicsPatientDetailPage from './pages/clinics/ClinicsPatientDetailPage';
import ClinicsAppointmentsPage from './pages/clinics/ClinicsAppointmentsPage';
import ClinicsRoomHistoryPage from './pages/clinics/ClinicsRoomHistoryPage';
import ClinicsMedialHistoryChatPage from './pages/clinics/ClinicsMedialHistoryChatPage';
import ClinicsDoctorsPage from './pages/clinics/ClinicsDoctorsPage';
import InvoicesPage from './pages/invoices/InvoicesPage';

import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminComerciosPage from './pages/admin/AdminComerciosPage';
import AdminComercioBillingPage from './pages/admin/AdminComercioBillingPage';
import AdminBotStatsPage from './pages/admin/AdminBotStatsPage';
import AdminBillingPage from './pages/admin/AdminBillingPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './components/AdminLayout';
import AdminChatsPage from './pages/admin/AdminChatsPage';
import FeatureGuard from './components/FeatureGuard';

import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import EndUserDataDeletionPage from './pages/legal/EndUserDataDeletionPage';
import TenantDataDeletionPage from './pages/legal/TenantDataDeletionPage';
import RequestAccessPage from './pages/public/RequestAccessPage';
import AccessPendingPage from './pages/AccessPendingPage';
import AdminAvailableUsersPage from './pages/admin/AdminAvailableUsersPage';
import AdminDesignSystemPage from './pages/admin/AdminDesignSystemPage';
import LandingPage from './pages/public/LandingPage';
import { PublicLayout } from './components/layout/PublicLayout';
import SettingsLayout from './pages/settings/SettingsLayout';
import GeneralSettings from './pages/settings/GeneralSettings';
import CommentAutomations from './pages/settings/CommentAutomations';
import PostBotPage from './pages/PostBotPage';
import LegalSettings from './pages/settings/LegalSettings';
import { useAppSelector } from './store/hooks';
import { OnboardingGuard } from './components/onboarding/OnboardingGuard';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

import './index.css';
import { useTranslation } from 'react-i18next';

// Root route logic
const RootRoute: React.FC = () => {
  const loading = useAppSelector((s) => s.auth.loading);
  const user = useAppSelector((s) => s.auth.user);
  const { t } = useTranslation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0f0f13] text-white/70">{t('app.loading')}</div>;
  }

  if (user) {
    return <Navigate to="/stats" replace />;
  }

  return <LandingPage />;
};

// Protected route for Super Admin
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children as React.ReactElement;
};

const ProtectedLayout = () => {
  return (
    <ProtectedRoute>
      <OnboardingGuard>
        <Layout>
          <Outlet />
        </Layout>
      </OnboardingGuard>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <>
      <svg className="absolute w-0 h-0 pointer-events-none">
        <defs>
          {/* White inner glow — use with light-colored icons */}
          <filter id="icon-inner-glow-white" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
            <feComposite in="blur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="innerEdgeMask" />
            <feFlood floodColor="white" floodOpacity="0.85" result="whiteGlow" />
            <feComposite in="whiteGlow" in2="innerEdgeMask" operator="in" result="whiteInnerEdge" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="whiteInnerEdge" />
            </feMerge>
          </filter>
          {/* Tiny white inner glow — keeps small colored icons visibly filled */}
          <filter id="icon-inner-glow-white-tiny" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="0.45" result="tinyBlur" />
            <feComposite in="tinyBlur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="tinyInnerEdgeMask" />
            <feFlood floodColor="white" floodOpacity="0.45" result="tinyWhiteGlow" />
            <feComposite in="tinyWhiteGlow" in2="tinyInnerEdgeMask" operator="in" result="tinyWhiteInnerEdge" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="tinyWhiteInnerEdge" />
            </feMerge>
          </filter>
          {/* Black inner glow — use with dark-colored icons */}
          <filter id="icon-inner-glow-black" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
            <feComposite in="blur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="innerEdgeMask" />
            <feFlood floodColor="black" floodOpacity="0.85" result="blackGlow" />
            <feComposite in="blackGlow" in2="innerEdgeMask" operator="in" result="blackInnerEdge" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="blackInnerEdge" />
            </feMerge>
          </filter>
          {/* Blue inner glow — use with white icons to create blue inner edge glow */}
          <filter id="icon-inner-glow-blue" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
            <feComposite in="blur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="innerEdgeMask" />
            <feFlood floodColor="#3b82f6" floodOpacity="0.95" result="blueGlow" />
            <feComposite in="blueGlow" in2="innerEdgeMask" operator="in" result="blueInnerEdge" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="blueInnerEdge" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/access-pending" element={<AccessPendingPage />} />
          <Route path="/payment-result" element={<PaymentResultPage />} />

          {/* Public Legal & Landing Pages wrapped in PublicLayout */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<RootRoute />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/data-deletion/users" element={<EndUserDataDeletionPage />} />
            <Route path="/data-deletion/clinics" element={<TenantDataDeletionPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
          </Route>

          {/* Routes with ProtectedRoute and ProtectedShell */}
          <Route element={<ProtectedLayout />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/products" element={<FeatureGuard featureKey="products"><ProductsPage /></FeatureGuard>} />
            <Route path="/orders" element={<FeatureGuard featureKey="orders"><OrdersPage /></FeatureGuard>} />
            <Route path="/order-detail/:id" element={<FeatureGuard featureKey="orders"><OrderDetailPage /></FeatureGuard>} />
            <Route path="/delivery-settings" element={<FeatureGuard featureKey="delivery_settings"><CommerceDeliverySettingsPage /></FeatureGuard>} />
            <Route path="/test-api" element={<FeatureGuard featureKey="test_api"><TestApiPage /></FeatureGuard>} />
            <Route path="/bots-config" element={<FeatureGuard featureKey="chatbots"><BotsConfigPage /></FeatureGuard>} />
            <Route path="/bots-config/:botId" element={<FeatureGuard featureKey="chatbots"><BotDetailPage /></FeatureGuard>} />
            <Route path="/workflows" element={<CommentAutomations />} />
            <Route path="/post-bot" element={<FeatureGuard featureKey="chatbots"><PostBotPage /></FeatureGuard>} />
            <Route path="/stats" element={<FeatureGuard featureKey="chatbots"><BotStatsPage /></FeatureGuard>} />
            <Route path="/stats/chat-bots" element={<FeatureGuard featureKey="chatbots"><BotStatsPage /></FeatureGuard>} />
            <Route path="/stats/clinic-chats" element={<ClinicChatsStatsPage />} />
            <Route path="/tags-config" element={<FeatureGuard featureKey="tags"><TagsConfigPage /></FeatureGuard>} />
            
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="general" replace />} />
              <Route path="general" element={<GeneralSettings />} />
              <Route path="automations" element={<Navigate to="/workflows" replace />} />
              <Route path="connections" element={<ConnectionsPage />} />
              <Route path="legal" element={<LegalSettings />} />
              <Route path="meta-legal" element={<Navigate to="/settings/legal" replace />} />
            </Route>

            <Route path="/invoices" element={<FeatureGuard featureKey="invoices"><InvoicesPage /></FeatureGuard>} />
            <Route path="/connections" element={<Navigate to="/settings/connections" replace />} />

            <Route path="/clinics" element={<Navigate to="/clinics/dashboard" replace />} />
            <Route path="/clinics/dashboard" element={<FeatureGuard featureKey="clinics"><ClinicsDashboardPage /></FeatureGuard>} />
            <Route path="/clinics/doctors" element={<FeatureGuard featureKey="clinics"><ClinicsDoctorsPage /></FeatureGuard>} />
            <Route path="/clinics/patients" element={<FeatureGuard featureKey="clinics"><ClinicsPatientsPage /></FeatureGuard>} />
            <Route path="/clinics/patients/:id" element={<FeatureGuard featureKey="clinics"><ClinicsPatientDetailPage /></FeatureGuard>} />
            <Route path="/clinics/patients/:id/chat" element={<FeatureGuard featureKey="clinics"><ClinicsMedialHistoryChatPage /></FeatureGuard>} />
            <Route path="/clinics/appointments" element={<FeatureGuard featureKey="clinics"><ClinicsAppointmentsPage /></FeatureGuard>} />
            <Route path="/clinics/rooms/:id/history" element={<FeatureGuard featureKey="clinics"><ClinicsRoomHistoryPage /></FeatureGuard>} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminLayout><AdminDashboardPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/comercios" element={<AdminProtectedRoute><AdminLayout><AdminComerciosPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/comercios/:comercioKey/billing" element={<AdminProtectedRoute><AdminLayout><AdminComercioBillingPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/comercios/:comercioKey/chats" element={<AdminProtectedRoute><AdminLayout><AdminChatsPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/bot-stats" element={<AdminProtectedRoute><AdminLayout><AdminBotStatsPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/billing" element={<AdminProtectedRoute><AdminLayout><AdminBillingPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/available-users" element={<AdminProtectedRoute><AdminLayout><AdminAvailableUsersPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/design-system" element={<AdminProtectedRoute><AdminLayout><AdminDesignSystemPage /></AdminLayout></AdminProtectedRoute>} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
